import {Button} from '@udemy-v2/react-core-components';
import React, {useMemo, useSyncExternalStore} from 'react';
import {useI18n} from '@udemy-v2/i18n';

import {reportAbuseLink} from './urls';
// import getConfigData from 'utils/get-config-data';

import styles from './report-abuse.module.scss';
import {useCommonData} from '@udemy-v2/common-data';

export interface ReportAbuseProps {
    className?: string;
    fullWidthButton?: boolean;
    objectId: number;
    objectType: string;
    size?: 'medium' | 'large';
    text?: string;
    udStyle?: 'secondary' | 'ghost';
    onClick?: () => void;
    withDivider?: boolean;
}

export const ReportAbuse: React.FC<ReportAbuseProps> = ({
    className = '',
    fullWidthButton = false,
    objectId,
    objectType,
    size = 'medium',
    text: textProp,
    udStyle = 'ghost',
    onClick = () => {},
    withDivider = false,
}) => {
    const {gettext, locale} = useI18n();
    const text = textProp ?? gettext('Report abuse');
    const {user} = useCommonData();

    // Use useSyncExternalStore for hydration-safe URL access
    // Server renders with '/', client uses actual window.location.href
    const returnUrl = useSyncExternalStore(
        () => () => {}, // subscribe function (no-op since location doesn't change within component)
        () => window.location.href, // getSnapshot (client value)
        () => '/', // getServerSnapshot (server value)
    );

    const url = useMemo(() => {
        const source = 'course_landing_page';

        const reportAbuseFormFullUrl = reportAbuseLink({
            objectId,
            objectType,
            extraParams: {source, return_link: returnUrl},
        });

        if (user) {
            return reportAbuseFormFullUrl;
        }

        // Unauthenticated user: construct a *relative* login URL
        const loginPath = '/join/passwordless-auth/';

        // 1. The 'next' parameter is simply the relative reportAbuseFormFullUrl.
        const nextParamValue = reportAbuseFormFullUrl; // e.g., "/feedback/report/?foo=bar"

        // 2. Construct the relative auth URL using URLSearchParams for query params.
        const authUrlSearchParams = new URLSearchParams();
        authUrlSearchParams.append('locale', locale || 'en_US');
        authUrlSearchParams.append('next', nextParamValue);
        authUrlSearchParams.append('response_type', 'json');
        authUrlSearchParams.append('action', 'login');
        authUrlSearchParams.append('mode', '');

        return `${loginPath}?${authUrlSearchParams.toString()}`; // e.g., "/join/passwordless-auth/?..."
    }, [objectId, objectType, returnUrl, user, locale]);

    // TODO: Find the correct source for config and feature flags
    // if (!getConfigData().features.report_abuse) {
    //     return null;
    // }

    const button = (
        <Button
            className={className}
            componentClass="a"
            href={url}
            size={size}
            udStyle={udStyle}
            onClick={onClick}
            data-purpose="report-abuse-link"
        >
            {text}
        </Button>
    );

    if (fullWidthButton) {
        return (
            <div
                className={`${styles.componentMargin} ${styles.componentBorder} ${withDivider ? styles.divider : ''}`}
            >
                <div className={styles['report-abuse-full-width']}>{button}</div>
            </div>
        );
    }

    return button;
};
